/**
 * Google Maps Loader Utility
 * 
 * Utility to load Google Maps JavaScript API using Dynamic Library Import
 * with the bootstrap loader pattern for optimal performance.
 */

declare global {
  interface Window {
    google: typeof google;
  }
}

let isBootstrapLoaded = false;

/**
 * Initialize the Google Maps bootstrap loader
 * This should be called once, preferably early in the app lifecycle
 */
export function initializeGoogleMapsBootstrap(): void {
  if (isBootstrapLoaded) {
    console.log("ℹ️ Google Maps bootstrap already loaded, skipping initialization");
    return;
  }

  console.log("🔄 Initializing Google Maps bootstrap loader...");

  const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY;

  if (!apiKey) {
    console.error("❌ Google Maps API key not found");
    console.error("Please set VITE_GOOGLE_MAPS_API_KEY in your .env file");
    return;
  }

  console.log("✅ Google Maps API key found:", apiKey.substring(0, 10) + "...");

  // Bootstrap loader inline script (from Google Maps documentation)
  (function (g) {
    var h: Promise<void> | null = null;
    var a: HTMLScriptElement | null = null;
    var k: string;
    var p = "The Google Maps JavaScript API";
    var c = "google";
    var l = "importLibrary";
    var q = "__ib__";
    var m = document;
    var b = window;
    b = (b[c] || (b[c] = {})) as typeof google;
    var d = (b.maps || (b.maps = {})) as typeof google.maps;
    var r = new Set<string>();
    var e = new URLSearchParams();
    var u = () =>
      h ||
      (h = new Promise(async (f, n) => {
        a = m.createElement("script");
        e.set("libraries", [...r] + "");
        for (k in g) {
          e.set(
            k.replace(/[A-Z]/g, (t) => "_" + t[0].toLowerCase()),
            g[k as keyof typeof g] as string
          );
        }
        e.set("callback", c + ".maps." + q);
        (a as HTMLScriptElement).src = `https://maps.${c}apis.com/maps/api/js?` + e;
        d[q] = f as () => void;
        (a as HTMLScriptElement).onerror = () =>
          (h = n(Error(p + " could not load.")));
        (a as HTMLScriptElement).nonce =
          m.querySelector("script[nonce]")?.nonce || "";
        m.head.append(a as HTMLScriptElement);
      }));
    if ((d as any)[l]) {
      console.warn("⚠️ " + p + " only loads once. Ignoring:", g);
    } else {
      (d as any)[l] = (f: string, ...n: unknown[]) => {
        console.log("📚 Loading Google Maps library:", f);
        r.add(f) && u().then(() => {
          console.log("✅ Google Maps library loaded:", f);
          (d as any)[l](f, ...n);
        }).catch((error) => {
          console.error("❌ Error loading Google Maps library:", f, error);
          throw error;
        });
      };
    }
  })({
    key: apiKey,
    v: "weekly",
  });

  isBootstrapLoaded = true;
  console.log("✅ Google Maps bootstrap loader initialized");
}

/**
 * Load a Google Maps library using Dynamic Library Import
 * @param library - The library name (e.g., 'maps', 'places', 'marker')
 * @returns Promise that resolves when the library is loaded
 */
export async function loadGoogleMapsLibrary(
  library: "maps" | "places" | "marker" | "geocoding" | "routes" | "geometry" | "drawing"
): Promise<void> {
  console.log(`🔄 loadGoogleMapsLibrary called for library: ${library}`);
  
  initializeGoogleMapsBootstrap();

  if (!window.google?.maps?.importLibrary) {
    console.error("❌ Google Maps bootstrap loader not initialized");
    console.error("window.google:", window.google);
    console.error("window.google.maps:", window.google?.maps);
    throw new Error("Google Maps bootstrap loader not initialized");
  }

  console.log(`📚 Calling importLibrary for: ${library}`);
  try {
    await window.google.maps.importLibrary(library);
    console.log(`✅ Successfully loaded library: ${library}`);
    
    // Log what's available after loading
    if (library === "places" && window.google?.maps?.places) {
      console.log("✅ Places library loaded, available APIs:", Object.keys(window.google.maps.places));
    }
  } catch (error) {
    console.error(`❌ Error loading library ${library}:`, error);
    console.error("Error details:", {
      message: error instanceof Error ? error.message : String(error),
      stack: error instanceof Error ? error.stack : undefined,
    });
    throw error;
  }
}

/**
 * Check if Google Maps is already loaded
 */
export function isGoogleMapsLoaded(): boolean {
  return typeof window.google !== "undefined" && !!window.google.maps;
}

